import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from './Layout'; // Import the Layout component
import { useNavigate } from 'react-router-dom';
import { mcc } from '../Mcc';
import { useAuth } from './StoreValue';
import { markStepComplete } from './StepGuard';
import { getAgentConfig, isPromotix } from '../utils/agentConfig';

const BusinessInfoForm = () => {
    const [BusinessInfo, setBusinessInfo] = useState({
        dateBusinessEstablished: null,
        ownershipType: '',
        industry: '',
        businessLocation: '',
        businessHours: '',
        businessStartHours: '08:00',
        businessEndHours: '12:00',
        merchandiseType: '',
        merchandiseFullfillment: '',
        deliverySpan: '',
        returnPolicy: '',
        settlementPolicy: ''
    });
    const [BankInfo, setBankInfo] = useState({
        merchantCategoryCode: '' // or some default value
      });
      
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    const { ContactGuid,agentIdVal,leadGuid, updateAgentId, updateContactGuid, updateLeadGuid } = useAuth();
    const [showVendorFields, setShowVendorFields] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const navigate = useNavigate();

    const handleChange = (e) => {
        const { name, value } = e.target;
        setBusinessInfo((prevInfo) => ({
            ...prevInfo,
            [name]: value,
        }));
    };
    
    const handleBankChange = (e) => {
        const { name, value } = e.target;
        setBankInfo((prevInfo) => ({
            ...prevInfo,
            [name]: value,
        }));
    };

    
  const agentId = getSessionStorageWithExpiry("agentId");
   // Check if agentId exists in REVIO array
  
  const agentIdString = agentId ? agentId.toString() : '';

  // PRIORITY FLAGS derived from centralized agent config (mutually exclusive by construction)
  const agentConfig = getAgentConfig(agentIdString);
  const { business: businessConfig, completionPath } = agentConfig;
  const isPromotixAgent = isPromotix(agentIdString);
  const isEnsoraAgent  = businessConfig.mccFilter === 'fixed';
  const isMWRestricted = businessConfig.mccDefault === '8661';
  const isMWRevio   = isMWRestricted && completionPath === 'extended';
  const isMWGeneral = isMWRestricted && completionPath === 'standard';
  const isRevIO     = Array.isArray(businessConfig.mccFilter) && !isMWRestricted && !isPromotixAgent;
  const isEDining   = businessConfig.lockFields === true;
  const isNormal    = !isPromotixAgent && !isEnsoraAgent && !isMWRestricted && !isRevIO && !isEDining;

  useEffect(() => {
    if ((isMWRevio || isMWGeneral) && !BusinessInfo.ownershipType) {
      setBusinessInfo(prev => ({ ...prev, ownershipType: 'NonProfit' }));
    }
  }, [isMWRevio, isMWGeneral]);

  useEffect(() => {
    if (businessConfig.mccDefault && !BankInfo.merchantCategoryCode) {
      setBankInfo(prev => ({ ...prev, merchantCategoryCode: businessConfig.mccDefault }));
    }
  }, [businessConfig.mccDefault]);



    
    useEffect(() => {
        const fetchLeadData = async () => {
            try {
                const response = await axios.get(`${API_BASE_URL}/api/Auth/LeadLookup`, {
                params: {
                    leadGuidVal: leadGuid,
                    agentIdValue: agentIdVal,
                    // Pass leadGuid here
                },
                });
                const leadData = response.data;
                const leadLookupresponseCode = leadData.result.responseCode;

                if (leadLookupresponseCode === '00') {
                    const fetchedBusiness = leadData.queryResponse.business;
                    const fetachBank = leadData.queryResponse.bank;
                    const businessHoursStr = fetchedBusiness.businessHours || '';
                    const hoursParts = businessHoursStr.includes(' - ') 
                        ? businessHoursStr.split(' - ') 
                        : businessHoursStr.split('-');
                    
                    setBusinessInfo({
                        dateEstablished: formatDateToYYYYMMDD(fetchedBusiness.dateEstablished || ''),
                        ownershipType: fetchedBusiness.ownershipType || ((isMWRevio || isMWGeneral) ? 'NonProfit' : ''),
                        industry: fetchedBusiness.industry || '',
                        businessLocation: fetchedBusiness.businessLocation || '',
                        businessHours: businessHoursStr,
                        businessStartHours: padHourToTwoDigits((hoursParts[0] || '').trim()) || '08:00',
                        businessEndHours: (hoursParts[1] || '').trim() || '12:00',
                        merchandise: fetchedBusiness.merchandise || '',
                        merchandiseFullfillment: fetchedBusiness.merchandiseFullfillment || '',
                        merchandiseDeliverySpan: fetchedBusiness.merchandiseDeliverySpan || '',
                        returnPolicy: fetchedBusiness.returnPolicy || '',
                        settlementPolicy: fetchedBusiness.settlementPolicy || '',
                    });

                    setBankInfo({
                        merchantCategoryCode: (isMWRevio || isMWGeneral)
                            ? (fetachBank?.merchantCategoryCode || '8661')
                            : (fetachBank?.merchantCategoryCode || ''),
                    });
                }
            } catch (error) {
                console.error('Error fetching lead data:', error);
            }
        };

        fetchLeadData();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrorMessage('');
        setSuccessMessage('');
        if (!leadGuid) {
            setErrorMessage('Session expired or lead data is missing. Please start over from the login page.');
            return;
        }

        // Apply agent-specific values
        const isRevioStyle = isRevIO || isMWRevio || isMWGeneral;
        const updatedBusinessInfo = {
            ...BusinessInfo,
            industry:                isPromotixAgent ? "Retail"        : isEnsoraAgent ? "Retail"          : isEDining ? "CardNotPresent"  : isRevioStyle ? "CardNotPresent"  : BusinessInfo.industry,
            merchandise:             isPromotixAgent ? "Ticket Sales"  : isEnsoraAgent ? "Medical Services" : isEDining ? "Restaurant"      : BusinessInfo.merchandise,
            returnPolicy:            isPromotixAgent ? "NoReturn"      : isEnsoraAgent ? "NoReturn"         : isEDining ? "NoReturn"        : isRevioStyle ? "NoReturn"        : BusinessInfo.returnPolicy,
            settlementPolicy:        isPromotixAgent ? "OrderDate"     : isEnsoraAgent ? "OrderDate"        : isEDining ? "OrderDate"       : isRevioStyle ? "OrderDate"       : BusinessInfo.settlementPolicy,
            merchandiseFullfillment: isPromotixAgent ? "Merchant"      : isEnsoraAgent ? "Merchant"         : isEDining ? "Merchant"        : isRevioStyle ? "Merchant"        : BusinessInfo.merchandiseFullfillment,
            merchandiseDeliverySpan: isPromotixAgent ? "1"             : isEnsoraAgent ? "1"                : isEDining ? "1"               : isRevioStyle ? "1"               : BusinessInfo.merchandiseDeliverySpan,
            businessLocation:        isPromotixAgent ? "Office"        : isEnsoraAgent ? "Office"           : isEDining ? "StoreFront"      : isRevioStyle ? "Office"          : BusinessInfo.businessLocation,
        };

        setBusinessInfo(updatedBusinessInfo);

        const data = {
            leadguidVal: leadGuid,
            agentId: agentIdVal,
            ContactGuidVal:ContactGuid,
            Detail: {
                TranType: "TranType",
                TranAction: "TranAction",
                Ip: 'Ip'
            },
            Business: {
                ...updatedBusinessInfo,
                shareSensitiveData: 0,
                dateEstablished: formatDateToMMDDYYYY(updatedBusinessInfo.dateEstablished),
                businessHours: formatTimeForSubmission(updatedBusinessInfo.businessStartHours) + '-' + formatTimeForSubmission(updatedBusinessInfo.businessEndHours)
            },
            Bank: {
                merchantCategoryCode: BankInfo.merchantCategoryCode
            }
        };

        try {
            const response = await axios.post(`${API_BASE_URL}/api/Auth/LeadModify`, data);
            const responseCode = response.data?.response?.result?.responseCode;
            const errorInfo = response.data?.response?.result?.debugInfo;

            if (responseCode === '00' || responseCode === 'X7') {
                markStepComplete('businessInfo');
                navigate('/VolumeInfo');
            } else {
                setErrorMessage(errorInfo || (typeof response.data === 'string' ? response.data : "Submission failed. Please try again."));
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            const serverMsg = error.response?.data?.message || error.response?.data || error.message;
            setErrorMessage(typeof serverMsg === 'string' ? serverMsg : 'Failed to submit form. Please try again.');
        }
    };


    const formatDateToMMDDYYYY = (date) => {
        const [year, month, day] = date.split("-");
        return `${month}/${day}/${year}`;
      };
    
      const formatDateToYYYYMMDD = (date) => {
        const [month, day, year] = date.split('/');
        return `${year}-${month}-${day}`;
      };

      const [startHour, setStartHour] = useState({ hour: "8:00", period: "AM" });
  const [endHour, setEndHour] = useState({ hour: "4:00", period: "PM" });

  const hours = Array.from({ length: 12 }, (_, i) => (i + 1).toString());

  const handleTimeChange = (field, value) => {
    // Convert 12-hour format to 24-hour format for PM hours
    let convertedValue = value;
    if (field === 'businessEndHours') {
      if (value === '12:00') {
        convertedValue = '12:00';
      } else {
        // Convert 1:00-11:00 to 13:00-23:00
        const hour = parseInt(value.split(':')[0]);
        if (hour >= 1 && hour <= 11) {
          convertedValue = `${hour + 12}:00`;
        }
      }
    }
    setBusinessInfo((prev) => ({
        ...prev,
        [field]: convertedValue,
    }));
};

  // Normalize "8:00" → "08:00" so it matches the Start Hours dropdown's option values
  const padHourToTwoDigits = (time) => {
    if (!time) return time;
    const match = time.match(/^(\d{1,2}):(\d{2})$/);
    if (!match) return time;
    return `${match[1].padStart(2, '0')}:${match[2]}`;
  };

  // Helper function to convert 24-hour format to 12-hour format for display
  const convert24To12Hour = (time24) => {
    if (!time24) return '12:00';
    if (time24 === '12:00') return '12:00';
    const hour = parseInt(time24.split(':')[0]);
    if (hour >= 13 && hour <= 23) {
      return `${hour - 12}:00`;
    }
    return time24;
};

  // Helper function to convert time to 12-hour format for submission (removes leading zeros)
  const formatTimeForSubmission = (time24) => {
    if (!time24) return '12:00';
    const hour = parseInt(time24.split(':')[0]);
    if (hour === 0 || hour === 12) {
      return '12:00';
    }
    if (hour >= 13 && hour <= 23) {
      // PM hours: convert 13-23 to 1-11
      return `${hour - 12}:00`;
    }
    // AM hours: remove leading zero (01:00 -> 1:00, 02:00 -> 2:00, etc.)
    return `${hour}:00`;
};

function getSessionStorageWithExpiry(key) {
    const itemStr = sessionStorage.getItem(key);
    if (!itemStr) return null; // Handle missing key
  
    const itemObj = JSON.parse(itemStr);
    if (!itemObj || !itemObj.value || !itemObj.expiry) return null; // Handle invalid object
  
    const now = new Date();
    if (now.getTime() > itemObj.expiry) {
      sessionStorage.removeItem(key); // Remove expired item
      return null;
    }
  
    return itemObj.value; // Return the value
  }

  return (
    <Layout pageTitle="Business Information">
      <form onSubmit={handleSubmit}>

        {/* ================= REVIO / MEMBER.WORKS LAYOUT ================= */}
        {(isRevIO || isMWRevio || isMWGeneral) && (
          <>
            {/* ROW 1 */}
            <div className="row">
              <div className="col-md-4">
                <label>Date Business Established <span style={{color:'red'}}>*</span></label>
                <input
                  type="date"
                  name="dateEstablished"
                  className="form-controling"
                  value={BusinessInfo.dateEstablished}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="col-md-4">
                <label>Type of Ownership <span style={{color:'red'}}>*</span></label>
                <select
                  name="ownershipType"
                  className="form-controling"
                  value={BusinessInfo.ownershipType}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select</option>
                  <option value="Corporation">Corporation</option>
                  <option value="LLC">LLC</option>
                  <option value="SoleProprietor">Sole Proprietor</option>
                  <option value="Government">Government</option>
                  <option value="NonProfit">Non Profit</option>
                  <option value="Partnership">Partnership</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="col-md-4">
                <label>Merchandise Type <span style={{color:'red'}}>*</span></label>
                <input
                  type="text"
                  name="merchandise"
                  className="form-controling"
                  value={BusinessInfo.merchandise}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            {/* ROW 2 */}
            <div className="row">
              <div className="col-md-4">
                <label>MCC <span style={{color:'red'}}>*</span></label>
                {(isMWRevio || isMWGeneral) ? (
                  <select
                    name="merchantCategoryCode"
                    className="form-controling"
                    value={BankInfo.merchantCategoryCode}
                    onChange={handleBankChange}
                    required
                  >
                    <option value="8641">CIVIC &amp; SOCIAL ASSOC</option>
                    <option value="8661">RELIGIOUS ORGANIZATIONS</option>
                  </select>
                ) : (
                  <select
                    name="merchantCategoryCode"
                    className="form-controling"
                    value={BankInfo.merchantCategoryCode}
                    onChange={handleBankChange}
                    required
                  >
                    <option value="">Select MCC</option>
                    {/* Config-driven: Rev.io = 7399/4812/4814/7393, In-Telecom = 4812/4814/7393 */}
                    {mcc.filter((m) => businessConfig.mccFilter.includes(m.code)).map((m) => (
                      <option key={m.code} value={m.code}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div className="col-md-4">
                <label>Start Business Hours (AM) EST</label>
                <select
                  className="form-controling"
                  value={BusinessInfo.businessStartHours}
                  onChange={(e) =>
                    setBusinessInfo({ ...BusinessInfo, businessStartHours: e.target.value })
                  }
                >
                  {['01:00', '02:00', '03:00', '04:00', '05:00', '06:00', '07:00', '08:00', '09:00', '10:00', '11:00', '12:00'].map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>

              <div className="col-md-4">
                <label className='label_Control' htmlFor="endBusinessHours">End Business Hours (PM) EST</label>
                <select
                  id="endBusinessHours"
                  value={convert24To12Hour(BusinessInfo.businessEndHours)}
                  className="form-controling"
                  onChange={(e) => handleTimeChange('businessEndHours', e.target.value)}
                >
                  {[...Array(12).keys()].map((hour) => {
                    const time = hour === 0 ? '12:00' : `${hour}:00`;
                    return (
                      <option key={hour} value={time}>
                        {time}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>
          </>
        )}

        {/* ================= ENSORA LAYOUT ================= */}
        {isEnsoraAgent && (
          <>
            <div className="row">
              <div className="col-md-4">
                <label>Date Business Established <span style={{color:'red'}}>*</span></label>
                <input
                  type="date"
                  name="dateEstablished"
                  className="form-controling"
                  value={BusinessInfo.dateEstablished}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="col-md-4">
                <label>Type of Ownership <span style={{color:'red'}}>*</span></label>
                <select
                  name="ownershipType"
                  className="form-controling"
                  value={BusinessInfo.ownershipType}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select</option>
                  <option value="Corporation">Corporation</option>
                  <option value="LLC">LLC</option>
                  <option value="SoleProprietor">Sole Proprietor</option>
                  <option value="Government">Government</option>
                  <option value="NonProfit">Non Profit</option>
                  <option value="Partnership">Partnership</option>
                  <option value="Other">Other</option>
                </select>
              </div>

            </div>

            <div className="row">
              <div className="col-md-4">
                <label>Start Business Hours (AM) EST</label>
                <select
                  className="form-controling"
                  value={BusinessInfo.businessStartHours}
                  onChange={(e) => setBusinessInfo({ ...BusinessInfo, businessStartHours: e.target.value })}
                >
                  {['01:00', '02:00', '03:00', '04:00', '05:00', '06:00', '07:00', '08:00', '09:00', '10:00', '11:00', '12:00'].map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>

              <div className="col-md-4">
                <label className='label_Control' htmlFor="endBusinessHoursEnsora">End Business Hours (PM) EST</label>
                <select
                  id="endBusinessHoursEnsora"
                  value={convert24To12Hour(BusinessInfo.businessEndHours)}
                  className="form-controling"
                  onChange={(e) => handleTimeChange('businessEndHours', e.target.value)}
                >
                  {[...Array(12).keys()].map((hour) => {
                    const time = hour === 0 ? '12:00' : `${hour}:00`;
                    return <option key={hour} value={time}>{time}</option>;
                  })}
                </select>
              </div>
            </div>
          </>
        )}

        {/* ================= PROMOTIX LAYOUT ================= */}
        {isPromotixAgent && (
          /* Only the genuinely editable fields are rendered. Industry, Business Location,
             Merchandise, Fulfillment, Delivery Span, Return Policy and Settlement Policy are
             fixed for PromoTix and were previously shown disabled — they are hidden now to
             reduce form fatigue. Their values are still submitted: handleSubmit forces all
             seven for isPromotixAgent regardless of what is rendered. */
          <>
            {/* ROW 1: Date Established | Ownership | MCC */}
            <div className="row">
              <div className="col-md-4">
                <label>Date Business Established <span style={{color:'red'}}>*</span></label>
                <input
                  type="date"
                  name="dateEstablished"
                  className="form-controling"
                  value={BusinessInfo.dateEstablished}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="col-md-4">
                <label>Type of Ownership <span style={{color:'red'}}>*</span></label>
                <select
                  name="ownershipType"
                  className="form-controling"
                  value={BusinessInfo.ownershipType}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select</option>
                  <option value="Corporation">Corporation</option>
                  <option value="LLC">LLC</option>
                  <option value="SoleProprietor">Sole Proprietor</option>
                  <option value="Government">Government</option>
                  <option value="NonProfit">Non Profit</option>
                  <option value="Partnership">Partnership</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="col-md-4">
                <label>MCC <span style={{color:'red'}}>*</span></label>
                <select
                  name="merchantCategoryCode"
                  className="form-controling"
                  value={BankInfo.merchantCategoryCode}
                  onChange={handleBankChange}
                  required
                >
                  <option value="">Select MCC</option>
                  {mcc.filter((m) => businessConfig.mccFilter.includes(m.code)).map((m) => (
                    <option key={m.code} value={m.code}>
                      {m.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* ROW 2: Start Hours | End Hours */}
            <div className="row">
              <div className="col-md-4">
                <label>Start Business Hours (AM) EST</label>
                <select
                  className="form-controling"
                  value={BusinessInfo.businessStartHours}
                  onChange={(e) =>
                    setBusinessInfo({ ...BusinessInfo, businessStartHours: e.target.value })
                  }
                >
                  {['01:00', '02:00', '03:00', '04:00', '05:00', '06:00', '07:00', '08:00', '09:00', '10:00', '11:00', '12:00'].map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>

              <div className="col-md-4">
                <label className='label_Control' htmlFor="endBusinessHoursPromotix">End Business Hours (PM) EST</label>
                <select
                  id="endBusinessHoursPromotix"
                  value={convert24To12Hour(BusinessInfo.businessEndHours)}
                  className="form-controling"
                  onChange={(e) => handleTimeChange('businessEndHours', e.target.value)}
                >
                  {[...Array(12).keys()].map((hour) => {
                    const time = hour === 0 ? '12:00' : `${hour}:00`;
                    return <option key={hour} value={time}>{time}</option>;
                  })}
                </select>
              </div>
            </div>
          </>
        )}

        {/* ================= EDINING / NORMAL ================= */}
        {!isRevIO && !isMWRevio && !isMWGeneral && !isEnsoraAgent && !isPromotixAgent && (
          <>
            {/* ROW 1 */}
            <div className="row">
              <div className="col-md-4">
                <label>Date Business Established <span style={{color:'red'}}>*</span></label>
                <input
                  type="date"
                  name="dateEstablished"
                  className="form-controling"
                  value={BusinessInfo.dateEstablished}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="col-md-4">
                <label>Type of Ownership <span style={{color:'red'}}>*</span></label>
                <select
                  name="ownershipType"
                  className="form-controling"
                  value={BusinessInfo.ownershipType}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select</option>
                  <option value="Corporation">Corporation</option>
                  <option value="LLC">LLC</option>
                  <option value="SoleProprietor">Sole Proprietor</option>
                  <option value="Government">Government</option>
                  <option value="NonProfit">Non Profit</option>
                  <option value="Partnership">Partnership</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="col-md-4">
                <label>Merchant Type / Industry {isNormal && <span style={{color:'red'}}>*</span>}</label>
                <select
                  name="industry"
                  className="form-controling"
                  value={isEDining ? 'CardNotPresent' : BusinessInfo.industry}
                  onChange={handleChange}
                  disabled={isEDining}
                  required={isNormal}
                >
                  <option value="">Select</option>
                  <option value="CardNotPresent">Card Not Present</option>
                  <option value="Restaurant">Restaurant</option>
                  <option value="Retail">Retail</option>
                </select>
              </div>
            </div>

            {/* ROW 2 */}
            <div className="row">
              <div className="col-md-4">
                <label>Business Location</label>
                <select
                  name="businessLocation"
                  className="form-controling"
                  value={isEDining ? 'StoreFront' : BusinessInfo.businessLocation}
                  onChange={handleChange}
                  disabled={isEDining}
                >
                  <option value="">Select</option>
                  <option value="StoreFront">Store Front</option>
                  <option value="Office">Office</option>
                </select>
              </div>

              <div className="col-md-4">
                <label>Start Business Hours (AM) EST</label>
                <select
                  className="form-controling"
                  value={BusinessInfo.businessStartHours}
                  onChange={(e) =>
                    setBusinessInfo({ ...BusinessInfo, businessStartHours: e.target.value })
                  }
                >
                  {['01:00', '02:00', '03:00', '04:00', '05:00', '06:00', '07:00', '08:00', '09:00', '10:00', '11:00', '12:00'].map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>

              <div className="col-md-4">
                <label className='label_Control' htmlFor="endBusinessHoursEDining">End Business Hours (PM) EST</label>
                <select
                  id="endBusinessHoursEDining"
                  value={convert24To12Hour(BusinessInfo.businessEndHours)}
                  className="form-controling"
                  onChange={(e) => handleTimeChange('businessEndHours', e.target.value)}
                >
                  {[...Array(12).keys()].map((hour) => {
                    const time = hour === 0 ? '12:00' : `${hour}:00`;
                    return (
                      <option key={hour} value={time}>
                        {time}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>

            {/* ROW 3 */}
            <div className="row">
              <div className="col-md-4">
                <label>Merchandise Type <span style={{color:'red'}}>*</span></label>
                <input
                  className="form-controling"
                  value={isEDining ? 'Restaurant' : BusinessInfo.merchandise}
                  onChange={handleChange}
                  name="merchandise"
                  disabled={isEDining}
                  required={isNormal}
                />
              </div>

              <div className="col-md-4">
                <label>Customer Return Policy <span style={{color:'red'}}>*</span></label>
                <select
                  name="returnPolicy"
                  className="form-controling"
                  value={isEDining ? 'NoReturn' : BusinessInfo.returnPolicy}
                  onChange={handleChange}
                  disabled={isEDining}
                  required={isNormal}
                >
                  <option value="">Select</option>
                  <option value="NoReturn">No Return</option>
                  <option value="30DayReturn">30 Day Return</option>
                  <option value="RMA">RMA</option>
                  <option value="ExchangeOnly">Exchange Only</option>
                  <option value="StoreCredit">Store Credit</option>
                  <option value="DefectiveOnly">Defective Only</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="col-md-4">
                <label>Customer Settlement Policy</label>
                <select
                  name="settlementPolicy"
                  className="form-controling"
                  value={isEDining ? 'OrderDate' : BusinessInfo.settlementPolicy}
                  onChange={handleChange}
                  disabled={isEDining}
                >
                  <option value="">Select</option>
                  <option value="OrderDate">Order Date</option>
                  <option value="DeliveryDate">Delivery Date</option>
                </select>
              </div>
            </div>

            {/* ROW 4 */}
            <div className="row">
              <div className="col-md-4">
                <label>MCC <span style={{color:'red'}}>*</span></label>
                <select
                  name="merchantCategoryCode"
                  className="form-controling"
                  value={BankInfo.merchantCategoryCode}
                  onChange={handleBankChange}
                  required
                >
                  <option value="">Select MCC</option>
                  {mcc.map((m) => (
                    <option key={m.code} value={m.code}>
                      {m.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-4">
                <label>Who performs service fulfillment?</label>
                <select
                  name="merchandiseFullfillment"
                  className="form-controling"
                  value={isEDining ? 'Merchant' : BusinessInfo.merchandiseFullfillment}
                  onChange={handleChange}
                  disabled={isEDining}
                >
                  <option value="">Select</option>
                  <option value="Merchant">Merchant</option>
                  <option value="Vendor">Vendor</option>
                </select>
              </div>

              <div className="col-md-4">
                <label>Number of days until service is delivered</label>
                <input
                  type="number"
                  name="merchandiseDeliverySpan"
                  className="form-controling"
                  value={isEDining ? '1' : BusinessInfo.merchandiseDeliverySpan}
                  onChange={handleChange}
                  disabled={isEDining}
                  min="0"
                />
              </div>
            </div>
          </>
        )}

        <div className="text-center mt-4">
          <button className="btn btn-success px-5">Next</button>
        </div>

        <div className="text-center mt-2">
          <small className="text-danger">* Required field</small>
        </div>

        {errorMessage && <p className="error-message">{errorMessage}</p>}
      </form>
    </Layout>
  );
};

export default BusinessInfoForm;

